import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Shirt, Coffee, Zap, Flag, CheckCircle, ChevronLeft, ChevronRight,
  Upload, ShieldCheck, Clock, Award, Sparkles, Star, ArrowLeft, CreditCard, ExternalLink, Mail
} from 'lucide-react';

const PAYMENT_URL =
  'https://www.convergepay.com/hosted-payments?ssl_txn_auth_token=t7W3lXTxSgyB82kRQyf4TgAAAZ3%2FQFuf';

const BANNER_QUOTE_MAILTO =
  "https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Custom Banner / Signage Quote Request&body=Hi Avid Branding,%0D%0A%0D%0AI'd like a quote for custom banners / signage.%0D%0A%0D%0APlease attach your logo to this email and describe what you'd like below:%0D%0A%0D%0A- What do you need (banner, sign, etc.):%0D%0A- Size / dimensions:%0D%0A- Quantity:%0D%0A- Indoor or outdoor:%0D%0A- Deadline:%0D%0A- Any other details:%0D%0A%0D%0AName:%0D%0APhone:%0D%0ACompany:%0D%0A%0D%0A(Don't forget to attach your logo file!)%0D%0A%0D%0AThank you!";

const SERVICES = [
  { id: 'apparel-print', icon: Shirt, title: 'Custom Apparel — Printed', desc: 'T-shirts, hoodies & more with vibrant printed designs' },
  { id: 'apparel-embroidery', icon: Coffee, title: 'Custom Apparel — Embroidered', desc: 'Polos, hats & caps with premium embroidery' },
  { id: 'dtf', icon: Zap, title: 'DTF Heat Transfers', desc: 'Order online — direct-to-film transfers, 24hr turnaround', externalUrl: 'http://www.dtfprintnearme.ca' },
  { id: 'banners', icon: Flag, title: 'Custom Banners & Signage', desc: 'Email us for a custom quote — attach your logo & details', emailQuote: true },
];

const GARMENTS = ['T-Shirt', 'Hoodie', 'Polo Shirt', 'Hat / Cap', 'Crewneck', 'Other'];
const COLORS = ['White', 'Black', 'Royal Blue', 'Sky Blue', 'Teal', 'Grey', 'Red', 'Other'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const BUNDLES = [
  {
    id: 'starter', name: 'Starter Bundle', price: '$197', items: '5 Custom Items',
    perks: ['5 custom apparel pieces', 'Your logo on every item', 'Free design proof', 'Standard turnaround'],
    highlight: false,
  },
  {
    id: 'pro', name: 'Pro Bundle', price: '$397', items: '12 Custom Items',
    perks: ['12 custom apparel pieces', 'Your logo on every item', 'Free design proof', 'Priority turnaround', 'Best value per item'],
    highlight: true,
  },
];

const STEPS = ['Service', 'Options', 'Logo', 'Why Us', 'Bundle'];

export const OrderFunnel = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [order, setOrder] = useState({
    service: '',
    garment: '',
    color: '',
    sizes: [],
    quantity: '',
    notes: '',
    bundle: '',
  });

  const update = (field, value) => setOrder((p) => ({ ...p, [field]: value }));

  const toggleSize = (size) => {
    setOrder((p) => ({
      ...p,
      sizes: p.sizes.includes(size) ? p.sizes.filter((s) => s !== size) : [...p.sizes, size],
    }));
  };

  const next = () => {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const serviceTitle = SERVICES.find((s) => s.id === order.service)?.title || 'Not selected';

  const logoMailto = () => {
    const body = `Hi Avid Branding,%0D%0A%0D%0AI'm placing a custom order and attaching my logo file to this email.%0D%0A%0D%0A--- My Order ---%0D%0AService: ${serviceTitle}%0D%0AGarment: ${order.garment || 'N/A'}%0D%0AColor: ${order.color || 'N/A'}%0D%0ASizes: ${order.sizes.join(', ') || 'N/A'}%0D%0AQuantity: ${order.quantity || 'N/A'}%0D%0ANotes: ${order.notes || 'N/A'}%0D%0A%0D%0AName:%0D%0APhone:%0D%0ACompany:%0D%0A%0D%0A(Logo file attached)%0D%0A%0D%0AThank you!`;
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Custom Order — Logo Submission&body=${body}`,
      '_blank'
    );
  };

  const canProceed = () => {
    if (step === 0) return !!order.service;
    if (step === 1) return !!order.garment && order.sizes.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50">
      {/* Top bar */}
      <div className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <button
            onClick={() => navigate('/')}
            data-testid="funnel-back-home-button"
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            <ArrowLeft className="h-5 w-5 mr-2" /> Back to Home
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/5p8whwdm_avid-race.png"
            alt="Avid Branding Logo"
            className="h-10 w-auto"
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="funnel-title">
            Start Your <span className="text-blue-600">Custom Order</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A few quick steps to bring your brand to life. No minimum orders, quality guaranteed.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-10" data-testid="funnel-progress">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                      i < step
                        ? 'bg-teal-500 text-white'
                        : i === step
                        ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {i < step ? <CheckCircle className="h-5 w-5" /> : i + 1}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${i === step ? 'text-blue-600' : 'text-gray-500'}`}>
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-1 rounded transition-all duration-300 ${i < step ? 'bg-teal-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* STEP 1 — Service Selection */}
        {step === 0 && (
          <div data-testid="funnel-step-service">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What can we make for you?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {SERVICES.map((s) => {
                const selected = order.service === s.id;
                return (
                  <Card
                    key={s.id}
                    onClick={() =>
                      s.externalUrl
                        ? window.open(s.externalUrl, '_blank', 'noopener,noreferrer')
                        : s.emailQuote
                        ? window.open(BANNER_QUOTE_MAILTO, '_blank')
                        : update('service', s.id)
                    }
                    data-testid={`service-option-${s.id}`}
                    className={`cursor-pointer p-6 border-2 transition-all duration-200 ${
                      selected ? 'border-blue-600 bg-blue-50 shadow-lg' : 'border-blue-100 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-xl ${selected ? 'bg-blue-600' : 'bg-blue-100'}`}>
                        <s.icon className={`h-6 w-6 ${selected ? 'text-white' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{s.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{s.desc}</p>
                      </div>
                      {selected && <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0" />}
                      {s.externalUrl && <ExternalLink className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                      {s.emailQuote && <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2 — Product Options */}
        {step === 1 && (
          <div data-testid="funnel-step-options">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Customize your product</h2>
            <Card className="p-6 sm:p-8 border-2 border-blue-100 space-y-8">
              <div>
                <Label className="text-base font-semibold text-gray-900 mb-3 block">Garment Type</Label>
                <div className="flex flex-wrap gap-3">
                  {GARMENTS.map((g) => (
                    <button
                      key={g}
                      onClick={() => update('garment', g)}
                      data-testid={`garment-${g.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                        order.garment === g ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 text-gray-700 hover:border-blue-300'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold text-gray-900 mb-3 block">Color</Label>
                <div className="flex flex-wrap gap-3">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => update('color', c)}
                      data-testid={`color-${c.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                        order.color === c ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-200 text-gray-700 hover:border-teal-300'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold text-gray-900 mb-3 block">Sizes Needed</Label>
                <div className="flex flex-wrap gap-3">
                  {SIZES.map((sz) => (
                    <button
                      key={sz}
                      onClick={() => toggleSize(sz)}
                      data-testid={`size-${sz.toLowerCase()}`}
                      className={`w-14 h-11 rounded-lg border-2 text-sm font-semibold transition-all duration-200 ${
                        order.sizes.includes(sz) ? 'border-sky-600 bg-sky-500 text-white' : 'border-gray-200 text-gray-700 hover:border-sky-300'
                      }`}
                    >
                      {sz}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="quantity" className="text-base font-semibold text-gray-900 mb-2 block">Estimated Quantity</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    placeholder="e.g. 10"
                    value={order.quantity}
                    onChange={(e) => update('quantity', e.target.value)}
                    data-testid="quantity-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-base font-semibold text-gray-900 mb-2 block">Project Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Tell us about your design ideas, placement, deadlines..."
                  value={order.notes}
                  onChange={(e) => update('notes', e.target.value)}
                  data-testid="notes-input"
                  rows={3}
                />
              </div>
            </Card>
          </div>
        )}

        {/* STEP 3 — Logo Upload */}
        {step === 2 && (
          <div data-testid="funnel-step-logo">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Send us your logo</h2>
            <Card className="p-8 border-2 border-blue-100 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Upload className="h-10 w-10 text-blue-600" />
              </div>
              <p className="text-lg text-gray-700 max-w-xl mx-auto mb-2">
                Click below to open a pre-filled email with your order details. Just
                <span className="font-semibold text-gray-900"> attach your logo file</span> and hit send.
              </p>
              <p className="text-sm text-gray-500 mb-8">Accepted: PNG, JPG, PDF, AI, EPS, or SVG</p>

              <Button
                onClick={logoMailto}
                data-testid="logo-email-button"
                className="bg-blue-600 hover:bg-blue-700 text-white py-4 px-8 text-lg font-semibold"
              >
                <Upload className="h-5 w-5 mr-2" /> Email My Logo & Order Details
              </Button>

              <div className="bg-blue-50 p-4 rounded-lg mt-8 max-w-md mx-auto">
                <p className="text-sm text-gray-600">
                  No logo yet? No problem — our team offers <span className="font-semibold">free brand & logo consultation</span>.
                  Just mention it in your email.
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* STEP 4 — Why Us / Persuasive */}
        {step === 3 && (
          <div data-testid="funnel-step-whyus">
            <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Why brands choose Avid</h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Known brands started here. We turn your vision into apparel people are proud to wear.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              {[
                { icon: ShieldCheck, t: '100% Quality Guarantee', d: 'Durable, moisture-wicking, premium fabrics every time.' },
                { icon: Clock, t: 'Fast Turnaround', d: 'DTF transfers in as little as 24 hours.' },
                { icon: Award, t: 'No Minimum Orders', d: 'Order 1 piece or 1,000 — we treat you the same.' },
                { icon: Sparkles, t: 'Free Design Proof', d: 'See and approve your design before we produce it.' },
              ].map((f) => (
                <Card key={f.t} className="p-6 border-2 border-blue-100 flex items-start space-x-4">
                  <div className="bg-teal-100 p-3 rounded-xl flex-shrink-0">
                    <f.icon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{f.t}</h3>
                    <p className="text-sm text-gray-600 mt-1">{f.d}</p>
                  </div>
                </Card>
              ))}
            </div>
            <Card className="p-8 bg-gradient-to-r from-blue-600 to-sky-500 text-white text-center">
              <div className="flex justify-center mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-white text-white" />)}
              </div>
              <p className="text-lg italic max-w-xl mx-auto">
                "Outstanding quality and the team made the whole process effortless. Our team gear looks incredible!"
              </p>
              <p className="mt-3 font-semibold text-blue-100">— A Happy Local Business Owner</p>
            </Card>
          </div>
        )}

        {/* STEP 5 — Bundle Offers + Checkout */}
        {step === 4 && (
          <div data-testid="funnel-step-bundle">
            <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Pick your bundle & save</h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Lock in the best value. Choose a bundle below and check out securely.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {BUNDLES.map((b) => {
                const selected = order.bundle === b.id;
                return (
                  <Card
                    key={b.id}
                    onClick={() => update('bundle', b.id)}
                    data-testid={`bundle-option-${b.id}`}
                    className={`cursor-pointer p-7 border-2 relative transition-all duration-200 ${
                      selected ? 'border-blue-600 bg-blue-50 shadow-xl scale-[1.02]' : 'border-blue-100 hover:border-blue-300 hover:shadow-lg'
                    } ${b.highlight ? 'ring-2 ring-teal-400' : ''}`}
                  >
                    {b.highlight && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-gray-900">{b.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{b.items}</p>
                    <p className="text-4xl font-extrabold text-blue-600 mb-4">{b.price}</p>
                    <ul className="space-y-2">
                      {b.perks.map((p) => (
                        <li key={p} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                    {selected && (
                      <div className="mt-4 flex items-center justify-center text-blue-600 font-semibold text-sm">
                        <CheckCircle className="h-5 w-5 mr-1" /> Selected
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <Card className="p-6 border-2 border-blue-100 bg-white">
              <h4 className="font-bold text-gray-900 mb-3">Order Summary</h4>
              <div className="text-sm text-gray-700 space-y-1 mb-5">
                <p><span className="font-medium">Service:</span> {serviceTitle}</p>
                <p><span className="font-medium">Garment:</span> {order.garment || 'N/A'} · {order.color || 'N/A'}</p>
                <p><span className="font-medium">Sizes:</span> {order.sizes.join(', ') || 'N/A'}</p>
                <p><span className="font-medium">Quantity:</span> {order.quantity || 'N/A'}</p>
                <p><span className="font-medium">Bundle:</span> {BUNDLES.find((b) => b.id === order.bundle)?.name || 'Not selected'}</p>
              </div>
              <Button
                onClick={() => window.open(PAYMENT_URL, '_blank', 'noopener,noreferrer')}
                disabled={!order.bundle}
                data-testid="checkout-button"
                className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white py-5 text-lg font-bold disabled:opacity-50"
              >
                <CreditCard className="h-5 w-5 mr-2" /> Proceed to Secure Checkout
              </Button>
              <p className="text-center text-xs text-gray-500 mt-3">
                🔒 You'll be redirected to our secure payment portal. Remember to email your logo (Step 3) so we can get started!
              </p>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-10">
          {step > 0 ? (
            <Button
              onClick={back}
              variant="outline"
              data-testid="funnel-prev-button"
              className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 py-5 px-6"
            >
              <ChevronLeft className="h-5 w-5 mr-1" /> Back
            </Button>
          ) : (
            <span />
          )}

          {step < STEPS.length - 1 && (
            <Button
              onClick={next}
              disabled={!canProceed()}
              data-testid="funnel-next-button"
              className="bg-blue-600 hover:bg-blue-700 text-white py-5 px-8 font-semibold disabled:opacity-50"
            >
              Continue <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
